import { useEffect, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { adminInviteService } from '../../services/adminService';
import { DataTable } from '../../components/ui/data-table';
import { toast } from 'react-hot-toast';

const columns: ColumnDef<any>[] = [
  {
    id: 'brand',
    header: 'Brand',
    cell: ({ row }) => {
      const i = row.original;
      if (!i.used) return <span>-</span>;
      return (
        <div className="flex items-center gap-3">
          {i.session?.brandDetail?.brandProfileImage ? (
            <img
              src={i.session.brandDetail.brandProfileImage}
              alt="brand"
              className="w-8 h-8 rounded object-cover border"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-muted text-muted-foreground flex items-center justify-center border">
              <span className="text-sm font-semibold">
                {(i.session?.brandDetail?.companyName || '?').charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <span>{i.session?.brandDetail?.companyName || '-'}</span>
        </div>
      );
    },
    meta: { width: 'w-56' },
  },
  {
    id: 'token',
    header: 'Token',
    cell: ({ row }) => (
      <span className="font-mono text-xs">{row.original.token}</span>
    ),
  },
  {
    id: 'shopUrl',
    header: 'Shop URL',
    cell: ({ row }) => <span>{row.original.shopUrl || '-'}</span>,
  },
  {
    id: 'trialDays',
    header: 'Trial Days',
    cell: ({ row }) => <span>{row.original.trialDays ?? 7} days</span>,
    meta: { width: 'w-28' },
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <span
        className={`inline-flex items-center px-2 py-1 rounded text-xs ${
          row.original.used
            ? 'bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-300'
            : 'bg-yellow-100 dark:bg-yellow-500/15 text-yellow-700 dark:text-yellow-300'
        }`}
      >
        {row.original.used ? 'Used' : 'Unused'}
      </span>
    ),
    meta: { width: 'w-24' },
  },
  {
    id: 'usedAt',
    header: 'Used At',
    cell: ({ row }) => (
      <span>
        {row.original.usedAt ? new Date(row.original.usedAt).toLocaleString() : '-'}
      </span>
    ),
    meta: { width: 'w-44' },
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => (
      <button
        onClick={async () => {
          await navigator.clipboard.writeText(
            `${window.location.origin}/admin-invite/${row.original.token}`
          );
          toast.success('Copied');
        }}
        className="px-3 py-1.5 border border-border rounded-lg hover:bg-accent"
      >
        Copy Link
      </button>
    ),
    meta: { width: 'w-32' },
  },
];

export function AdminInvites() {
  const [invites, setInvites] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [newInviteUrl, setNewInviteUrl] = useState('');
  const [trialDays, setTrialDays] = useState<number>(7);

  const load = async () => {
    try {
      setIsLoading(true);
      const [list, overview] = await Promise.all([
        adminInviteService.listInvites(),
        adminInviteService.getOverview(),
      ]);
      setInvites(list.items);
      setRemoteStats(overview);
    } catch (e: any) {
      toast.error('Failed to load invites');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    try {
      setCreating(true);
      const res = await adminInviteService.createInvite(trialDays);
      await load();
      setNewInviteUrl(res.url);
      setShowModal(true);
      try {
        await navigator.clipboard.writeText(res.url);
        toast.success('Invite created and copied to clipboard');
      } catch {
        toast.success('Invite created');
      }
    } catch (e: any) {
      toast.error('Failed to create invite');
    } finally {
      setCreating(false);
    }
  };

  const [remoteStats, setRemoteStats] = useState<{ total: number; used: number; unused: number; usageRate: number } | null>(null);

  return (
    <div className="space-y-6">
      {/* Header & Action */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Admin Invites</h1>
          <p className="text-sm text-muted-foreground">Generate single-use brand install links and track their usage.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <label htmlFor="trialDays" className="text-sm text-muted-foreground">Trial Days:</label>
            <input
              id="trialDays"
              type="number"
              min="1"
              max="30"
              value={trialDays}
              onChange={(e) => {
                const value = parseInt(e.target.value, 10);
                if (!isNaN(value) && value >= 1 && value <= 30) {
                  setTrialDays(value);
                }
              }}
              className="w-20 px-2 py-1.5 border border-border rounded-lg text-sm"
            />
          </div>
          <button
            onClick={create}
            disabled={creating}
            className="inline-flex items-center px-4 py-2 rounded-lg bg-[#d7785b] text-white hover:bg-[#c86c51] transition disabled:opacity-50"
          >
            {creating ? (
              <>
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Creating...
              </>
            ) : (
              'Generate Invite'
            )}
          </button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-muted-foreground text-sm">Total Invites</p>
          <p className="text-2xl font-bold text-foreground">{remoteStats?.total ?? 0}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-muted-foreground text-sm">Used</p>
          <p className="text-2xl font-bold text-foreground">{remoteStats?.used ?? 0}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-muted-foreground text-sm">Unused</p>
          <p className="text-2xl font-bold text-foreground">{remoteStats?.unused ?? 0}</p>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <p className="text-muted-foreground text-sm">Usage Rate</p>
          <p className="text-2xl font-bold text-foreground">{remoteStats?.usageRate ?? 0}%</p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
        <DataTable
          columns={columns}
          data={invites}
          isLoading={isLoading}
          getRowId={(row) => row.id}
          emptyText="No invites yet."
        />
      </div>

      {/* Invite Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowModal(false)} />
          <div className="relative bg-card rounded-xl border border-border shadow-xl w-full max-w-lg p-6">
            <h2 className="text-lg font-semibold mb-2">Invite Created</h2>
            <p className="text-sm text-muted-foreground mb-4">Share this single-use link with the brand. It will ask for their Shopify store URL.</p>
            <div className="flex items-center gap-2 mb-4">
              <input
                readOnly
                value={newInviteUrl}
                className="flex-1 px-3 py-2 border border-border rounded-lg bg-muted text-sm"
              />
              <button
                onClick={async () => { await navigator.clipboard.writeText(newInviteUrl); toast.success('Copied'); }}
                className="px-3 py-2 rounded-lg border border-border hover:bg-accent text-sm"
              >
                Copy
              </button>
            </div>
            <div className="flex justify-end gap-2">
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={newInviteUrl}
                className="px-4 py-2 rounded-lg bg-muted hover:bg-accent text-foreground"
              >
                Open Link
              </a>
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 rounded-lg bg-[#d7785b] text-white hover:bg-[#c86c51]"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


